import { BEAM_LEN, aimSlashFx } from '../world/player';
import { TAU, lerp } from '../core/math';
import { RNG, rr } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { scene } from '../render/scene';
import { angDiff, damageEnemy, heal, hitstop, nearestEnemy, shake } from './fx';
import { safeSpot } from '../world/arena';
import { P, beamPivot, slashFx, vortexRig } from '../world/player';
import { mechEvent } from '../entities/mech-anim';
import { CAM, camCine, camLow, camPunch } from './camera';
import { bullets, mines, missiles, strikes } from '../world/pools';
import { VC, fxBeamHit, fxExplode, fxImpact, fxLaunch, fxMuzzle, fxShockRing, fxSlashArc, fxSparks, fxUltFire } from '../render/weapon-fx';
import { updateBeam3D } from '../render/beam-fx';
import { screenFlash } from '../render/screen-flash';
import { fireMortar, fireRingBurst, fireRocket, updateRingBurst } from './weapons-titan';
/* ============================================================
   武裝
   ============================================================ */
export function spawnBullet(x, z, ang, dmg, crit, speed, pierce?, chain?, hitR?, scaleZ?, who?) {
  var b = bullets.get(); if (!b) return null;
  /* 出膛高度跟著**玩家腳下的地面**走：站在高台上開的槍要從高台的高度飛出去，
     否則子彈會從平台側面鑽出來，而且第一根柱子就把它吃掉。 */
  b.m.position.set(x, P.y + 2.1, z); b.m.rotation.set(0, ang, 0);
  if (hitR > 1) b.m.scale.set(hitR * 1.7, 0.8, 0.7); else b.m.scale.set(0.16, 0.16, scaleZ || 1.5);
  b.v.set(Math.sin(ang) * speed, 0, Math.cos(ang) * speed);
  b.dmg = dmg; b.crit = crit; b.pierce = pierce || 0; b.chain = chain || 0;
  b.hitR = hitR || 0.5; b.life = 0; b.hitList.length = 0; b.who = who || '';
  /* **每次都要清掉**：子彈是池化的，上一發的反彈次數留著的話
     下一發普通子彈會莫名其妙開始彈牆，而且完全不會有錯誤。
     （同型的坑在 sim/elites.ts 記過一次：池化物件的欄位沒清。） */
  b.bounce = 0; b.split = 0; b.ttl = 0;
  scene.add(b.m); return b;
}

export function fireVulcan() {
  var tgt = nearestEnemy(P.pos, P.s.atkRange, P.faceAim, true);
  if (!tgt) return;
  /* **只從持槍的那隻手射出**（2026-08-05 使用者拍板）。在這之前是左右交替
     （`side = P.barrel ? 1 : -1; P.barrel ^= 1;`）—— 配上 7.5 發/秒
     讀起來是一道連續的彈流，分不出是哪隻手打的。發彈頻率減半之後每一發都
     看得見，固定一側才有「這台機體舉起手在射」那個動作感。

     **方向的判準是模型上手臂的實際座標，不是座標系推理。**
     第一版我從 `(cos(aim), -sin(aim))` 推出「+1 是右方」就寫死 `side = 1`，
     實看是從**空著的那隻手**噴出子彈的。真正的依據在
     `entities/mechs.ts:82`：主武裝手臂掛在 `x = -1.02`，也就是 local **−x**。
     偏移向量 `side * (cos(aim), -sin(aim))` 剛好就是 local **+x** 方向
     （three.js 的 `rotation.y = θ` 會把 local +X 轉到 world `(cosθ, -sinθ)`），
     所以要對上那隻手臂，`side` 必須是 **−1**。

     ⚠️ 換機體要重新確認：`side` 綁的是**這台機體的手臂在哪一側**，
     不是一個通用的左右。LANCER-2 的手臂在 `x = +1.05`（`mechs.ts:202`），
     它如果之後也走這條路徑，符號要相反。
     `P.barrel` 不再被主砲使用（欄位留著，它進快照）。 */
  var side = -1;
  var dist = Math.hypot(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z);
  var tt = dist / P.s.atkSpeed;
  var aim = Math.atan2(tgt.pos.x + tgt.vel.x * tt * 0.6 - P.pos.x, tgt.pos.z + tgt.vel.z * tt * 0.6 - P.pos.z);
  var ox = P.pos.x + Math.sin(aim) * 1.2 + Math.cos(aim) * side * 1.35;
  var oz = P.pos.z + Math.cos(aim) * 1.2 - Math.sin(aim) * side * 1.35;
  var mz = P.rig.muzzles[side > 0 ? 1 : 0];
  if (mz) { mz.visible = true; mz.userData.t = 0.05; }
  var mod = P.mod.primary, count = P.s.atkCount;
  for (var i = 0; i < count; i++) {
    var spread = count === 1 ? rr(-0.018, 0.018) : (i - (count - 1) / 2) * 0.075 + rr(-0.012, 0.012);
    var crit = RNG.next() < P.s.crit;
    spawnBullet(ox, oz, aim + spread, P.s.atkDmg * (crit ? P.s.critMul : 1), crit, P.s.atkSpeed,
      mod === 'pierce' ? 3 : 0, mod === 'chain' ? 3 : 0, 0.5, undefined, 'mech-primary');
  }
  mechEvent(P.rig, 'fire');
  /* 槍口特效走 `render/weapon-fx.ts` 的配方（核心 + 十字閃光 + 噴焰面片）。
     rig 上原本那顆八面體閃光留著當「機體自己的燈」—— 它跟著手臂骨段轉，
     負責的是「這台機體正在開火」；這一組是世界空間的，負責「打出去了」。 */
  fxMuzzle(ox, P.y + 2.1, oz, aim, 0.35);
  CAM.push = Math.min(0.5, CAM.push + 0.16);   // 後座：鏡頭被推一下，齊射才有重量
  SFX.shoot(); shake(0.012);
}

/* ── 電磁刃：三段連斬（設計 `.claude/notes/lancer2-arts.md` §1）────────
   改版前每一刀完全一樣 —— 近戰遊戲最基本的爽感（節奏）缺席。
   現在是「橫斬 → 反手橫斬 → 下劈」，力量集中在第三下：

     第 1/2 段  弧 1.15  射程 ×1.00  傷害 ×0.75
     第 3 段    弧 0.95  射程 ×1.30  傷害 ×1.55 ＋ 頓幀／震動／濺花／前踏／衝擊 ×1.6

   總傷害 0.75+0.75+1.55 = 3.05，改版前 3 刀 ×1.0 = 3.00 → **中性**。
   改的是分佈不是強度（唯一的強度改動是第三段的衝擊係數，見 `sim/impact.ts`）。

   **打不到人也算一段**：敵人被擊退之後就打不到，不算的話第三段永遠出不來。 */
var COMBO = [
  { dmg: 0.75, arc: 1.15, rng: 1.00, step: 0 },
  { dmg: 0.75, arc: 1.15, rng: 1.00, step: 1 },
  { dmg: 1.55, arc: 0.95, rng: 1.30, step: 2, heavy: true }
];
/** 連段窗口（秒）。比揮擊間隔（2.7 發/秒 ≈ 0.37 秒）寬一些，
    但短到「站住不動」才接得完 —— 那個取捨才是近戰該有的決策。 */
var COMBO_WIN = 0.9;

export function fireBlade() {
  var mod = P.mod.primary;
  if (P.comboT <= 0) P.combo = 0;
  var C = COMBO[P.combo];
  P.comboT = COMBO_WIN;
  P.combo = (P.combo + 1) % COMBO.length;

  var range = P.s.atkRange * C.rng * (mod === 'wide' ? 1.45 : 1);
  var halfArc = (mod === 'wide' ? 1.75 : C.arc);
  var kb = (mod === 'wide' ? 16 : 7) * (C.heavy ? 1.8 : 1);
  var src = C.heavy ? 'bladeHeavy' : 'blade';
  var hits = 0;
  for (var i = enemies.length - 1; i >= 0; i--) {
    /* 連鎖移除防護：damageEnemy → killEnemy → areaDamage 可能一次移除好幾隻，
       陣列在迭代途中縮短。反向迭代只保證「移除當前元素」安全，
       不保證「一次移除多個」安全（2026-08-01 記過同型教訓）。 */
    if (i >= enemies.length) i = enemies.length - 1;
    if (i < 0) break;
    var e = enemies[i]; if (!e) continue;
    var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
    if (d > range + e.r) continue;
    if (Math.abs(angDiff(Math.atan2(dx, dz), P.faceAim)) > halfArc) continue;
    var crit = RNG.next() < P.s.crit;
    var k = 1 / Math.max(0.6, d);
    damageEnemy(e, P.s.atkDmg * C.dmg * (crit ? P.s.critMul : 1), crit,
      dx * k * kb, dz * k * kb, src, undefined, undefined, 'mech-primary');
    hits++;
    /* **每一次命中都要濺出來。** 改版前三招都只有「揮出去的軌跡」，
       沒有「砍到東西」的視覺 —— 那是近戰最缺的一塊（lancer2-arts.md §0）。 */
    fxImpact(e.pos.x, e.T.y + e.r * 0.6, e.pos.z, Math.atan2(dx, dz), crit || !!C.heavy);
  }

  P.swing = 1; mechEvent(P.rig, C.heavy ? 'heavy' : 'fire');
  slashFx.visible = true; slashFx.userData.life = 0;
  slashFx.position.set(P.pos.x, 1.6, P.pos.z);
  aimSlashFx(P.faceAim);
  slashFx.scale.set(range, 1, range);
  /* **普攻不畫 `fxSlashArc`**（使用者 2026-08-06：「把普攻的細長光效果拿掉」）。
     那個函式的第一段是「沿揮砍方向的一道細長弧」（`render/weapon-fx.ts:213`），
     每秒 2.7 次刷在畫面上太吵。命中的讀取現在靠**每一隻各一發 `fxImpact`**
     （這一輪新加的），那是打在敵人身上的、比一條掃過去的光更準確。
     突進斬與收刀仍然畫弧 —— 它們一次一拍，而且要讀出位移方向。 */
  if (mod === 'pulse') {
    var crit2 = RNG.next() < P.s.crit;
    spawnBullet(P.pos.x, P.pos.z, P.faceAim, P.s.atkDmg * 0.65 * (crit2 ? P.s.critMul : 1), crit2, 34, 99, 0, 2.4, 0.6, 'mech-primary');
  }
  SFX.slash(P.pos.x, P.pos.z, C.step);
  if (C.heavy) {
    /* 第三段是這一招的爽點：頓幀 + 重震 + 火星 + 往前踏。
       `docs/game-enjoy/06-game-feel.md` 的規格表把「暴擊」放在 3–4 幀，
       近戰的第三段是貼身 1.1 秒換來的，值得落在那一格。 */
    hitstop(0.62); shake(hits ? 0.20 : 0.07); camPunch(0.45);
    fxSparks(P.pos.x, P.y + 1.5, P.pos.z, 10, P.faceAim, 0.5, VC.fire, 22);
    /* 往前踏：連段本身帶一點位移，貼身戰不會變成原地黏著。
       走 `vel` 不走 `pos` —— 直接改位置會穿過障礙（碰撞是在 vel 之後解的）。 */
    P.vel.x += Math.sin(P.faceAim) * 5.2; P.vel.z += Math.cos(P.faceAim) * 5.2;
  } else {
    shake(hits ? 0.05 : 0.02);
  }
}

export function fireMissiles() {
  var mod = P.mod.mid;
  if (mod === 'mines') {
    for (var k = 0; k < 6; k++) {
      var mn = mines.get(); if (!mn) break;
      var a = (k / 6) * TAU + rr(-0.3, 0.3), d = rr(4, 7.5);
      var sp = safeSpot(P.pos.x + Math.cos(a) * d, P.pos.z + Math.sin(a) * d, 1);
      mn.m.position.set(sp.x, 0, sp.z); mn.m.scale.setScalar(0.01);
      mn.life = 0; mn.arm = 0.35; mn.dmg = P.s.midDmg * 0.85; mn.who = 'mech-mid';
      scene.add(mn.m);
    }
    SFX.missile(); shake(0.1);
    fxShockRing(P.pos.x, P.pos.z, VC.fire, 1, 9, 0.35);
    return;
  }
  var used = [];
  for (var i = 0; i < P.s.midCount; i++) {
    var m = missiles.get(); if (!m) break;
    var tgt = null;
    for (var tries = 0; tries < 10; tries++) {
      var c = nearestEnemy(P.pos, 44, P.faceAim + rr(-1.2, 1.2), false);
      if (!c) break;
      if (used.indexOf(c) < 0 || used.length >= enemies.length) { tgt = c; break; }
      used.push(c);
    }
    if (!tgt) tgt = nearestEnemy(P.pos, 60);
    if (tgt) used.push(tgt);
    m.m.position.set(P.pos.x + rr(-0.9, 0.9), 3.2, P.pos.z + rr(-0.9, 0.9));
    m.m.scale.setScalar(1);
    var a2 = P.faceAim + rr(-0.9, 0.9);
    m.v.set(Math.sin(a2) * 9, rr(11, 15), Math.cos(a2) * 9);
    m.target = tgt; m.life = 0; m.dmg = P.s.midDmg; m.radius = 3.4;
    m.split = (mod === 'cluster') ? 3 : 0; m.scale = 1; m.who = 'mech-mid';
    /* 池化物件的欄位每次都要清（同型的坑在 `spawnBullet` 的 `bounce` 記過）：
       留著上一發的緞帶編號會讓兩枚飛彈共用同一條，畫面上是一條在兩點間跳的線。 */
    m.trail = -1;
    scene.add(m.m);
  }
  mechEvent(P.rig, 'mid');
  SFX.missile(); shake(0.16);
  /* 齊射的「一團白煙從掛架噴出來」。飛彈本身有緞帶拖尾（`sim/projectiles.ts`），
     這裡負責發射的那一拍 —— 少了它，飛彈是憑空出現在半空中。 */
  fxLaunch(P.pos.x, P.y + 3.2, P.pos.z, P.faceAim, VC.fire);
  fxShockRing(P.pos.x, P.pos.z, VC.fire, 1.4, 7, 0.34, 0.5);
}

/* ── 突進斬：居合（設計 `.claude/notes/lancer2-arts.md` §2）──────────
   改版前所有回饋都散在 0.24 秒的衝刺裡，沒有任何一拍是重的。
   現在拆成兩拍：**衝過去（積蓄）→ 停下來（兌現）**。

     途中  每碰到一隻：×0.40 傷害（立即回饋，不能全延後）
     收刀  衝刺結束那一瞬間，對這趟砍中的每一隻追加 ×0.60 + 濺花 + 頓幀

   總傷害 0.40 + 0.60 = 1.00 × midDmg，與改版前完全相同。
   打空（一隻都沒碰到）**沒有收刀演出** —— 「有沒有砍到」在畫面上看得出來，
   不用看數字。收刀那一拍在 `sim/player.ts` 的衝刺結束分支。 */
export function fireDashSlash() {
  var mod = P.mod.mid;
  if (mod === 'hook') {
    for (var i = 0; i < enemies.length; i++) {
      var e = enemies[i]; if (e.T.boss) continue;
      var dx = P.pos.x - e.pos.x, dz = P.pos.z - e.pos.z, d = Math.hypot(dx, dz);
      if (d < 14 && d > 0.5) { e.vel.x += dx / d * 22; e.vel.z += dz / d * 22; }
    }
    fxShockRing(P.pos.x, P.pos.z, VC.fire, 14, 1, 0.35);
  }
  P.dashT = 0.24; P.dashAttack = true; P.slashHits.length = 0;
  P.dashDir.set(Math.sin(P.faceAim), 0, Math.cos(P.faceAim));
  P.iframe = Math.max(P.iframe, 0.36);
  slashFx.visible = true; slashFx.userData.life = 0;
  slashFx.position.set(P.pos.x, 1.6, P.pos.z);
  aimSlashFx(P.faceAim);
  slashFx.scale.set(6, 1, 6);
  /* 突進斬：弧比普通斬大一圈，另外加一道**沿衝刺方向**的速度線 ——
     它是位移技，讀者要看得出「往那邊衝過去了」而不只是「原地揮了一下」。 */
  fxSlashArc(P.pos.x, P.y + 1.6, P.pos.z, P.faceAim, 4.6, VC.fire);
  fxSparks(P.pos.x, P.y + 1.4, P.pos.z, 8, P.faceAim + Math.PI, 0.45, VC.fire, 26);
  mechEvent(P.rig, 'mid');
  /* 發動只給「衝出去」的震動，**重的那一下留給收刀**。
     兩拍都給大震動的話，玩家分不出哪一拍才是重點。 */
  SFX.dash(); SFX.slash(P.pos.x, P.pos.z, 0); shake(0.12);
}

/** 收刀：衝刺結束的那一拍，由 `sim/player.ts` 在 `dashT` 歸零時呼叫。
    `list` 是這一趟砍中的敵人（`P.slashHits`）。空的就什麼都不做 ——
    打空沒有兌現，那正是這一招要讀得出來的東西。 */
export function dashSlashFinish(list) {
  if (!list || !list.length) { shake(0.04); return; }
  var n = 0;
  for (var i = list.length - 1; i >= 0; i--) {
    var e = list[i];
    /* 清單裡的敵人可能已經在衝刺途中被打死並還回池子。
       `hp <= 0` 或不在場上就跳過 —— 對死掉的物件結算會把池子的欄位弄髒。 */
    if (!e || e.hp <= 0 || enemies.indexOf(e) < 0) continue;
    var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z;
    var crit = RNG.next() < P.s.crit;
    damageEnemy(e, P.s.midDmg * 0.60 * (crit ? P.s.critMul : 1), crit,
      P.dashDir.x * 6, P.dashDir.z * 6, 'dashSlash', undefined, undefined, 'mech-mid');
    fxImpact(e.pos.x, e.T.y + e.r * 0.6, e.pos.z, Math.atan2(dx, dz), true);
    n++;
  }
  if (!n) { shake(0.04); return; }
  /* 兌現的那一拍：橫貫的斬線 + 頓幀 + 重震 + 升到頂的音階。
     斬線畫在**衝刺的反方向**（收刀是往回收的動作）。 */
  slashFx.visible = true; slashFx.userData.life = 0;
  slashFx.position.set(P.pos.x, 1.6, P.pos.z);
  aimSlashFx(P.faceAim, Math.PI / 2);   // 收刀是橫過身體的一刀，轉 90°
  slashFx.scale.set(7.5, 1, 7.5);
  fxSlashArc(P.pos.x, P.y + 1.6, P.pos.z, P.faceAim + Math.PI / 2, 5.4, VC.fire);
  fxSparks(P.pos.x, P.y + 1.5, P.pos.z, 12, P.faceAim, 0.7, VC.fire, 24);
  hitstop(0.50); shake(0.30); camPunch(0.6);
  SFX.slash(P.pos.x, P.pos.z, 2);
  mechEvent(P.rig, 'heavy');
}

export function fireBeam() {
  var mod = P.mod.ult;
  if (mod === 'orbital') {
    var picks = [];
    for (var n = 0; n < 3; n++) {
      var best = null, bestC = -1;
      for (var i = 0; i < enemies.length; i++) {
        var e = enemies[i], c = 0;
        var tooClose = false;
        for (var p = 0; p < picks.length; p++) if (Math.hypot(e.pos.x - picks[p].x, e.pos.z - picks[p].z) < 10) tooClose = true;
        if (tooClose) continue;
        for (var j = 0; j < enemies.length; j++) if (Math.hypot(enemies[j].pos.x - e.pos.x, enemies[j].pos.z - e.pos.z) < 7) c++;
        if (e.T.boss) c += 6;
        if (c > bestC) { bestC = c; best = e; }
      }
      if (!best) break;
      picks.push({ x: best.pos.x, z: best.pos.z });
    }
    if (!picks.length) picks.push({ x: P.pos.x + Math.sin(P.faceAim) * 12, z: P.pos.z + Math.cos(P.faceAim) * 12 });
    for (var q = 0; q < picks.length; q++) {
      var s = strikes.get(); if (!s) break;
      s.m.position.set(picks[q].x, 0, picks[q].z);
      s.radius = 7; s.dmg = P.s.ultDmg * 5.5; s.life = 0; s.delay = 0.75 + q * 0.18; s.fired = false; s.who = 'mech-ult';
      s.ring.scale.setScalar(s.radius); s.ring.material.opacity = 0.8;
      s.col.scale.set(s.radius * 1.2, 60, s.radius * 1.2); s.col.material.opacity = 0;
      scene.add(s.m);
    }
    SFX.beam(); shake(0.4); camLow(0.9);
    return;
  }
  P.ultT = 0;
  beamPivot.visible = true;
  beamPivot.position.set(P.pos.x, 0, P.pos.z);
  beamPivot.rotation.y = P.faceAim;
  beamPivot.userData.tick = 0;
  P.ultTarget = (mod === 'focus') ? nearestEnemy(P.pos, 46, P.faceAim, true) : null;
  mechEvent(P.rig, 'ult');
  SFX.beam(); shake(0.7); camLow(1.2); camPunch(0.8);

  /* ---- 0.35 秒演出（使用者 2026-08-04 拍板的方向）----
     時間縮放 0.25 → 由 `simStep` 的 `damp(timeScale, 1, 9, dt)` 拉回，
     約 0.3 秒回到 1.0；推鏡 12%（`CAM.cine`，0.35 秒歸零）；
     全螢幕泛光 0.34 秒。三者的長度刻意對齊 —— 錯開的話會讀成三個獨立的抽搐。
     **不做黑邊／獨立鏡頭**：大招在倖存者類是高頻技能，每次都中斷戰鬥節奏會膩。 */
  hitstop(0.25); camCine(1); screenFlash('#bcd8f0', 0.24, 0.28);
  fxUltFire(P.pos.x, P.y + 2.3, P.pos.z, P.faceAim, VC.fire);
}

export function updateBeam(dt) {
  if (P.ultT < 0) return;
  var mod = P.mod.ult, dur = P.s.ultDur * (mod === 'focus' ? 1.7 : 1);
  P.ultT += dt;
  if (P.ultT >= dur) { P.ultT = -1; beamPivot.visible = false; return; }
  var k = P.ultT / dur;
  beamPivot.position.set(P.pos.x, 0, P.pos.z);
  if (mod === 'focus') {
    if (!P.ultTarget || !P.ultTarget.alive) P.ultTarget = nearestEnemy(P.pos, 46, P.faceAim, true);
    if (P.ultTarget) {
      var wa = Math.atan2(P.ultTarget.pos.x - P.pos.x, P.ultTarget.pos.z - P.pos.z);
      beamPivot.rotation.y += angDiff(wa, beamPivot.rotation.y) * Math.min(1, dt * 9);
    }
  } else {
    beamPivot.rotation.y = P.faceAim + k * TAU * 1.05;
  }
  /* 淡入只給 0.06 秒（原本 0.1）：大招要「轟」出來，不是「亮」出來。
     這 0.04 秒的差別在實拍上很明顯 —— 慢淡入會讓發射的那一拍軟掉。 */
  var fade = k < 0.06 ? k / 0.06 : (k > 0.85 ? (1 - k) / 0.15 : 1);
  var u = beamPivot.userData;
  var wMul = mod === 'focus' ? 1.5 + P.ultT * 0.5 : 1;
  updateBeam3D(u.rig, P.ultT, fade, wMul);
  u.tick -= dt;
  if (u.tick <= 0) {
    u.tick = 0.07;
    var ang = beamPivot.rotation.y, dx = Math.sin(ang), dz = Math.cos(ang);
    var half = (mod === 'focus' ? 2.4 : 1.7);
    var dmg = P.s.ultDmg * (mod === 'focus' ? (1 + P.ultT * 0.55) : 1);
    for (var i = enemies.length - 1; i >= 0; i--) {
      /* 連鎖移除防護：damageEnemy → killEnemy → areaDamage 可能一次移除好幾隻，
         陣列在迭代途中縮短。反向迭代只保證「移除當前元素」安全，
         不保證「一次移除多個」安全（2026-08-01 記過同型教訓）。 */
      if (i >= enemies.length) i = enemies.length - 1;
      if (i < 0) break;
      var e = enemies[i]; if (!e) continue;
      var rx = e.pos.x - P.pos.x, rz = e.pos.z - P.pos.z;
      var proj = rx * dx + rz * dz;
      if (proj < 0 || proj > BEAM_LEN) continue;
      if (Math.abs(rx * dz - rz * dx) < half + e.r) {
        damageEnemy(e, dmg, false, dx * 3, dz * 3, 'beam', undefined, undefined, 'mech-ult');
        // 落點閃光：少了它，光束掃過去只有數字在跳，看不出打在誰身上
        fxBeamHit(e.pos.x, e.T.y + e.r * 0.6, e.pos.z, 0x9fd0ff);
      }
    }
    shake(0.05);
  }
}

/* ── 旋刃：越打越猛 + 收束終結（設計 `.claude/notes/lancer2-arts.md` §3）──
   改版前的問題最嚴重：一台**高速近戰**機體的大招是「站著開四秒光環」，
   而且四秒之內畫面上沒有任何事情發生。它把這台機體的身份
   （貼身、位移、三段衝刺）整個關掉了。

   現在：期間移速 ×1.35，命中越多 `P.vortexSpin` 越高（轉更快、範圍更大、
   更亮、震動更重），時間到再收束成一次爆發。

   傷害：每拍 16（原本 20）× 33 拍 = 528，加收束 ultDmg ×(2+3·spin) = 40~100
   → 總量 568~628，原本是 660（全程滿場）。空砍更低、站對位置更高 ——
   GDD §6.2「大招的傷害由玩家的站位決定」。

   `spin` 是**純上行的**：不打人只是沒長大，沒有任何懲罰。
   「會自己漲、漲滿要罰」的累積型儀表在這個專案被否決過
   （`~/.claude/rules/lessons.md` 2026-08-01 熱量系統）—— 填滿是機會不是義務。 */
export function fireVortex() {
  P.ultT = 0; vortexRig.visible = true; vortexRig.userData.tick = 0;
  P.vortexSpin = 0; P.vortexBeat = 0;
  mechEvent(P.rig, 'ult');
  SFX.beam(); shake(0.35);
  fxShockRing(P.pos.x, P.pos.z, VC.fire, 1, 14, 0.5);
}

/** 大招期間的移速倍率。近戰的大招要能**追得到人**，不然它是一盞路燈。 */
export var VORTEX_SPD = 1.35;

export function updateVortex(dt) {
  if (P.ultT < 0) return;
  P.ultT += dt;
  var dur = P.s.ultDur;
  if (P.ultT >= dur) { vortexFinish(); return; }
  var k = P.ultT / dur;
  var spin = P.vortexSpin;
  var radius = (P.mod.ult === 'expand' ? lerp(7, 14, k) : 7) * (1 + 0.25 * spin);
  vortexRig.position.set(P.pos.x, 0, P.pos.z);
  vortexRig.rotation.y += dt * 9 * (1 + 1.4 * spin);      // 越打越快 —— 成長要看得見
  vortexRig.scale.setScalar(radius / 7);
  vortexRig.userData.mat.opacity = (0.5 + 0.28 * spin) * (k > 0.85 ? (1 - k) / 0.15 : 1);
  var u = vortexRig.userData;
  u.tick = (u.tick || 0) - dt;
  if (u.tick > 0) return;
  u.tick = 0.12;
  var healed = 0, hit = 0, far: any = null, farD = -1;
  for (var i = enemies.length - 1; i >= 0; i--) {
    if (i >= enemies.length) i = enemies.length - 1;
    if (i < 0) break;
    var e = enemies[i]; if (!e) continue;
    var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
    if (d < radius + e.r) {
      var kk = 1 / Math.max(0.6, d);
      damageEnemy(e, P.s.ultDmg * 0.8, false, dx * kk * 4, dz * kk * 4, 'vortex', undefined, undefined, 'mech-ult');
      if (P.mod.ult === 'absorb' && healed < 2) { heal(0.8); healed++; }
      hit++;
      if (d > farD) { farD = d; far = e; }
    }
  }
  /* 成長：命中數推 spin 往上，沒打到就停在原地慢慢掉一點點。
     一拍打到 4 隻就接近滿 —— 近戰的大招本來就是要開在人堆裡。 */
  P.vortexSpin = Math.max(0, Math.min(1, spin + (hit ? hit * 0.055 : -0.03)));
  if (!hit) return;
  /* 逐拍回饋。改版前這四秒完全沒有 shake/fx —— 打中人跟沒打中人
     在畫面上是同一件事，那是這一招最大的問題。 */
  shake(0.03 + 0.05 * P.vortexSpin);
  if (far) fxImpact(far.pos.x, far.T.y + far.r * 0.6, far.pos.z,
    Math.atan2(far.pos.x - P.pos.x, far.pos.z - P.pos.z), P.vortexSpin > 0.6);
  /* 每 0.36 秒一顆斬擊音（不是每拍 —— 每 0.12 秒一顆會變成噪音），
     音階隨 spin 往上走。 */
  P.vortexBeat -= 1;
  if (P.vortexBeat <= 0) {
    P.vortexBeat = 3;
    SFX.slash(P.pos.x, P.pos.z, P.vortexSpin > 0.66 ? 2 : (P.vortexSpin > 0.33 ? 1 : 0));
  }
}

/** 收束：時間到的那一瞬間，把四秒的累積一次放掉。
    **這是這一招的終結感來源** —— 改版前它只是靜靜地消失。 */
function vortexFinish() {
  var spin = P.vortexSpin;
  var radius = (P.mod.ult === 'expand' ? 14 : 7) * 1.7;
  var dmg = P.s.ultDmg * (2 + 3 * spin);
  var n = 0;
  for (var i = enemies.length - 1; i >= 0; i--) {
    if (i >= enemies.length) i = enemies.length - 1;
    if (i < 0) break;
    var e = enemies[i]; if (!e) continue;
    var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
    if (d >= radius + e.r) continue;
    var kk = 1 / Math.max(0.6, d);
    damageEnemy(e, dmg, true, dx * kk * 14, dz * kk * 14, 'vortex', undefined, undefined, 'mech-ult');
    fxImpact(e.pos.x, e.T.y + e.r * 0.6, e.pos.z, Math.atan2(dx, dz), true);
    n++;
  }
  fxExplode(P.pos.x, P.y + 1.4, P.pos.z, VC.fire, radius * 0.5, 0.7 + 0.5 * spin);
  fxShockRing(P.pos.x, P.pos.z, VC.fire, 2, radius, 0.42);
  hitstop(0.60); shake(0.35 + 0.25 * spin); camPunch(0.9);
  SFX.beam(); SFX.slash(P.pos.x, P.pos.z, 2);
  mechEvent(P.rig, 'heavy');
  P.ultT = -1; P.vortexSpin = 0; vortexRig.visible = false;
  return n;
}

export var WEAPONS = {
  vulcan: { auto: true, fire: fireVulcan },
  blade: { auto: true, fire: fireBlade },
  missile: { fire: fireMissiles },
  dashSlash: { fire: fireDashSlash },
  beam: { fire: fireBeam, update: updateBeam },
  vortex: { fire: fireVortex, update: updateVortex },
  /* TITAN-3 的三把在 `sim/weapons-titan.ts`（這個檔已經夠長了）。
     註冊表只有這一份 —— 機體資料的 `primary/mid/ult` 是這裡的 key。 */
  mortar: { auto: true, fire: fireMortar },
  rocket: { fire: fireRocket },
  ringBurst: { fire: fireRingBurst, update: updateRingBurst }
};
